/**
 * AtmosphereModule — the distance fog and the valley mist (Graphics G4;
 * FOG FROM THE SKY, 2026-09-25, realism §8 R5).
 *
 * `scene.fog` is ONE `FogExp2`, only ever MUTATED (swapping the object
 * rebuilds every material program — the pipeline rebuild law).
 *
 * COLOUR comes from the SKY, not a band table: the Hillaire horizon radiance
 * (lib/sky/sky_sampler, the ring 1° up averaged over azimuth with a touch of
 * the sun side) × E0 × the exposure curve's lux→scene factor — the colour the
 * terrain fades INTO is the sky behind it, at every hour, so the horizon seam
 * the old `#accfe6` / `#c98858` table drew is gone. Under a deck it greys
 * toward the CIE overcast horizon; a thick fog takes the diffuse light's own
 * grey; a sandstorm tints it (illumination-scaled by the same light).
 *
 * DENSITY: a constant clear-air haze + the meteo-resolved weather (biome mist,
 * radiation fog, precipitation haze, dust) + the weather kind. Where the
 * aerial-perspective froxels run (med+ WebGPU) the clear-air term is dropped
 * — the froxels ARE the clear-air haze, and both applied was double haze.
 *
 * THE VALLEY MIST: the analytic height fog's floor (`baseHeight`, a RENDER
 * space height) is anchored to the local terrain floor — the lowest point of a
 * 1 km window around the player, re-sampled as the player moves and re-folded
 * through `scene.position` every frame (the FP64 rebase moves render Y) — and
 * a dawn / after-rain MIST term from the meteo field's temperature–dew-point
 * spread and the wetness thickens it and lays 2–4 noise-free fog boxes in the
 * hollows. PostModule applies `heightFog` to the pass each frame.
 *
 * Lever: `__fog()`.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import type { DbConnection } from '../module_bindings/index.js';
import { atlas } from '../state.js';
import { farTierFog, fogWallDensity } from '../lib/terrain_far_tier.js';
import { worldEnv, TINT_BLEND } from '../lib/world_env.js';
import { bus } from '../bus.js';
import { activePreset, currentDrawDistance, distantDetailOn } from '../lib/quality_tiers.js';
import { resolveWeather, pickWeatherOverride, meteoAt } from '../lib/weather.js';
import { terrainHeight } from '@shared/terrain.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { lightFrame, exposureState, E0_LUX, overcastZenithRadiance, emissiveNits, lum709 } from '../lib/photometry.js';
import { fogSkyColor, mistAmount, CLEAR_AIR_HAZE, profileFogExcess, fogNightColor, fogThickness, skyFogShare } from '../lib/sky/fog_sky.js';
import { nightSkyLive, nightAir } from '../lib/sky/sky_sampler.js';
import { gameNightLift, cloudUplight, cloudNight } from '../lib/sky/cloud_light.js';
import { currentTod, uNightFactor } from './SkyModule.js';

/**
 * Underwater fog override (perf-critical contract): the scene's FogExp2 is
 * created ONCE and only ever MUTATED — swapping the `scene.fog` OBJECT forces
 * every material program to rebuild (a 1–2 s hitch on BOTH submerge and
 * surface). AtlasWaterModule sets/clears this instead of touching scene.fog.
 */
let underwaterOverride: { color: number; density: number } | null = null;
let refreshFog: (() => void) | null = null;

export function setUnderwaterFog(state: { color: number; density: number } | null): void {
  underwaterOverride = state;
  refreshFog?.();
}

/** The height fog's anchor + mist, in ABSOLUTE coordinates (PostModule folds scene.position in each frame). */
export interface HeightFogState { floorAbsY: number; mist: number; hollows: Array<{ x: number; y: number; z: number }>; sampledAt: { x: number; z: number } }

const FLOOR_WINDOW_M = 1000;
const FLOOR_GRID = 7;
const FLOOR_RESAMPLE_M = 120;

export class AtmosphereModule implements IModule {
  readonly name = 'Atmosphere';
  private scene!: THREE.Scene;
  private meteoTimer: number | null = null;
  private readonly fog = new THREE.FogExp2(0x88aacc, 0.0015);
  private subscribed = false;
  private app: App | null = null;
  private readonly _t = new THREE.Color();
  /** The height fog's terrain anchor and the mist (see the header). */
  readonly heightFog: HeightFogState = { floorAbsY: 0, mist: 0, hollows: [], sampledAt: { x: Number.NaN, z: Number.NaN } };
  private stats = { source: 'none', skyRadiance: [0, 0, 0] as number[], cloudiness: 0, aerial: false, weatherDensity: 0, night: [0, 0, 0] as number[], nightFactor: 0 };
  /**
   * THE TOWN'S GLOW IN THE MIST (the night-sky realism wave, 2026-10-06): the height fog's in-scatter lobe at night —
   * toward the strongest light dome (`skyglowLive().domes[0]`), coloured the fog plus the dome's excess on that bearing,
   * as narrow as the dome (power = 1/σ²). PostModule turns the pass's sun lobe to it while the sun's lobe is out
   * (fade 0); a misty night lights up toward a far town. Written at the fog's 1 Hz.
   */
  readonly nightLobe = { on: false, dir: new THREE.Vector3(1, 0.05, 0), color: new THREE.Color(), power: 8 };
  /**
   * THE SKY'S SHARE OF THE HEIGHT FOG (2026-10-06, agent S's summit finding): the pass's opacity over SKY pixels as a
   * fraction of its opacity over geometry (engine VolumetricFogPass `skyOpacity`). By day 1 (the pass as it was, bit for
   * bit). On a clear night 0 — the sky shader draws its own airmass extinction and horizon brightening, so a valley layer
   * the camera stands in (the floor resolved at a summit's foot) never paints the fog colour over the stars; a thick fog
   * (fog_sky.fogThickness of the weather) veils the sky again: fog_sky.skyFogShare = 1 − night × (1 − thick). Rain needs
   * no term — SkyModule's lit rain dome is the sky there (THE LIT RAIN DECK). PostModule writes it each frame.
   */
  skyFogOpacity = 1;
  /**
   * THE NIGHT IN-SCATTER (2026-10-06): the clear night horizon's radiance in scene units (the ring's median of the drawn
   * night sky × the night factor) — PostModule hands it to the aerial froxels' composite, whose baked in-scatter is the
   * sun's (black at night), so a clear night's far ridge fades to the night horizon, not to black. 0 by day.
   */
  readonly nightAerial = new THREE.Vector3();

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    this.scene.fog = this.fog;
    refreshFog = () => this.applyFromSky();
    bus.on('atlas:account-ready', () => { this.subscribe(); this.applyFromSky(); });
    for (const ev of ['atlas:environment-updated', 'atlas:world-entered'] as const) bus.on(ev, () => this.applyFromSky());
    // "Each second" (R5): the colour follows the sky's light (the curve moves it
    // continuously) and fronts move ~11 m/s — a 1 Hz re-apply tracks both.
    this.meteoTimer = window.setInterval(() => this.applyFromSky(), 1000);
    // THE NIGHT AIR's A/B (2026-10-06): false = the BEFORE (no night sky in the fog, no town glow on the ground, no
    // uplight, the clouds on the lifted floor) in the same frame; true = the wave; an object sets the four one by one.
    (globalThis as Record<string, unknown>).__nightAir = (o?: boolean | { fog?: boolean; glow?: boolean; uplight?: boolean; physical?: boolean }) => {
      const set = (v: { fog?: boolean; glow?: boolean; uplight?: boolean; physical?: boolean }): void => {
        if (typeof v.fog === 'boolean') nightAir.fog = v.fog;
        if (typeof v.glow === 'boolean') nightAir.glow = v.glow;
        if (typeof v.uplight === 'boolean') cloudUplight.enabled = v.uplight;
        if (typeof v.physical === 'boolean') cloudNight.physical = v.physical;
      };
      if (typeof o === 'boolean') set({ fog: o, glow: o, uplight: o, physical: o }); else if (o) set(o);
      this.applyFromSky();
      return { ...nightAir, uplight: cloudUplight.enabled, physical: cloudNight.physical };
    };
    (globalThis as Record<string, unknown>).__fog = () => ({
      color: this.fogColorHex, density: this.fog.density, ...this.stats,
      nightAir: { ...nightAir, uplight: cloudUplight.enabled, physicalClouds: cloudNight.physical },
      nightLobe: { on: this.nightLobe.on, dir: this.nightLobe.dir.toArray().map((v) => +v.toFixed(3)), color: `#${this.nightLobe.color.getHexString()}`, power: +this.nightLobe.power.toFixed(1) },
      skyFogOpacity: +this.skyFogOpacity.toFixed(3),
      heightFog: { floorAbsY: +this.heightFog.floorAbsY.toFixed(1), mist: +this.heightFog.mist.toFixed(3), hollows: this.heightFog.hollows.length },
    });
  }

  private subscribe(): void {
    if (this.subscribed) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.subscribed = true;
    conn.subscriptionBuilder()
      .onApplied(() => this.applyFromSky())
      .subscribe(['SELECT * FROM sky_state', 'SELECT * FROM weather_profile', 'SELECT * FROM world']);
  }

  private commons(conn: DbConnection): { timeOfDay: number; weather: string } | null {
    for (const w of conn.db.world.iter()) {
      if (w.kind === 'commons') { const s = conn.db.skyState.worldId.find(w.worldId); if (s) return { timeOfDay: s.timeOfDay, weather: s.weather }; }
    }
    return null;
  }

  private weatherProfile(conn: DbConnection, weather: string) {
    return [...conn.db.weatherProfile.iter()].find((p) => p.weather === weather);
  }

  private applyFromSky(): void {
    const conn = atlas.conn;
    const c = conn ? this.commons(conn) : null;
    if (conn && c) {
      // currentTod(): honors the dev/admin Local time override and per-world clocks.
      this.applyFog(currentTod(), c.weather, conn);
      return;
    }
    if (underwaterOverride) {
      this.fog.color.setHex(underwaterOverride.color);
      this.fog.density = underwaterOverride.density;
    }
  }

  /** The clear air is drawn by the aerial froxels (so the scene fog carries only the weather's excess) — read by the clouds' air. */
  get aerialOn(): boolean { return this.aerialLive(); }
  /** Is the aerial-perspective composite live (PostModule wired the froxels)? */
  private aerialLive(): boolean {
    const post = this.app?.getModule('Post') as { aerialLive?: boolean } | undefined;
    return !!post?.aerialLive;
  }

  /** Compose the fog: the sky's horizon light, the weather, the tier. */
  applyFog(tod: number, weather: string, conn: DbConnection): void {
    const seed = this.commonsSeed(conn);
    const wm = this.app?.getModule('Weather') as { debugWeather?: string | null } | undefined;
    const override = pickWeatherOverride(wm?.debugWeather ?? null, weather && weather !== 'clear' ? weather : undefined);
    const now = Date.now() / 1000;
    const resolved = resolveWeather(seed, atlas.localX, atlas.localZ, now, override, tod);
    // The live-ops profile's fog follows the SAME precedence as the resolved weather (2026-09-25, session 2):
    // a Local pin ('clear' by the lighting probe, the debug menu) wins over the server's kind — the profile of the
    // server's 'snow' (0.006/m) used to fog a pinned clear noon into a whiteout at 150 m.
    const wp = this.weatherProfile(conn, override ?? weather);
    // sky/post (2026-09-27): the row is SEED-RELATIVE (fog_sky.profileFogExcess) — the seeded 'clear' row's 0.0015/m put a
    // 1.3 km fog back over every clear day through the old max(); the resolver's visibility table owns the weather's fog.
    const kindDensity = wp ? profileFogExcess(wp.weather, wp.fogDensity) : 0;
    const weatherDensity = resolved.fogDensity + kindDensity;

    // ── COLOUR: the sky's horizon, in scene units ──
    const m = lightFrame.model;
    if (m) {
      const e0s = E0_LUX * exposureState.luxToScene;
      const ocHorizon = (overcastZenithRadiance(m.overcastLux) / 3) * exposureState.luxToScene;
      // THE NIGHT AIR (2026-10-06): the thick fog's light leaves out the game's night LIFT (the ground's dark-adapted floor
      // — it lit every new-moon mist a glowing grey, ~5× the ground): the night sky below carries the night, its natural
      // part at the sky shader's own display lift (night_sky_model.naturalSkyLift), so the mist agrees with the sky.
      const diffuseRad = (Math.max(0, m.globalLux - (nightAir.fog ? gameNightLift() : 0)) / Math.PI) * 0.85 * exposureState.luxToScene;
      const col = fogSkyColor({
        horizon: lightFrame.horizon, horizonSun: lightFrame.horizonSun, e0Scene: e0s,
        cloudiness: m.cloudiness, overcastHorizon: ocHorizon, fogDensity: weatherDensity, diffuseRadiance: diffuseRad,
      });
      this.fog.color.setRGB(col[0], col[1], col[2]);
      this.stats.source = 'sky'; this.stats.skyRadiance = col.map((v) => +v.toFixed(4)); this.stats.cloudiness = +m.cloudiness.toFixed(3);
      // THE NIGHT AIR (the night-sky realism wave, 2026-10-06): the Hillaire horizon above is black at night — the air takes
      // the NIGHT sky's light (sky_sampler.nightSkyLive: the natural sky + the contract's artificial sky, absolute cd/m²):
      // a clear haze the horizon ring's median, a thick fog the natural sky over it + the town's light as A DECK ON THE
      // GROUND (fog_sky.fogNightColor). Amber over a town, a dark blue-grey veil in the wilds. × the night factor (the
      // lamps are on), through emissiveNits (THE PHYSICAL-EMISSIVE CONTRACT).
      const nf = (uNightFactor as unknown as { value: number }).value;
      this.stats.nightFactor = +nf.toFixed(3);
      this.nightLobe.on = false;
      this.skyFogOpacity = nightAir.fog ? skyFogShare(nf, fogThickness(weatherDensity)) : 1;
      if (nf > 0 && nightAir.fog) {
        const ns = nightSkyLive(m.cloudiness);
        const nsDeck = nightSkyLive(1);
        const nc = fogNightColor(ns.horizonMedian, ns.meanNat, nsDeck.meanArt, weatherDensity);
        const toS = (nits: number): number => emissiveNits(nits) * nf;
        this.fog.color.r += toS(nc[0]); this.fog.color.g += toS(nc[1]); this.fog.color.b += toS(nc[2]);
        const hm = ns.horizonMedian;
        this.nightAerial.set(toS(hm.x), toS(hm.y), toS(hm.z));
        this.stats.night = nc.map((v) => +v.toExponential(3));
        // The lobe toward the strongest dome: the fog's colour + the dome's excess over the ring's median on that bearing.
        const dd = ns.domeDir;
        if (dd) {
          const ex = { x: Math.max(0, ns.horizonToward.x - ns.horizonMedian.x), y: Math.max(0, ns.horizonToward.y - ns.horizonMedian.y), z: Math.max(0, ns.horizonToward.z - ns.horizonMedian.z) };
          if (lum709(ex.x, ex.y, ex.z) > 0.05 * lum709(ns.horizonMedian.x, ns.horizonMedian.y, ns.horizonMedian.z) + 1e-7) {
            const L = this.nightLobe;
            L.on = true;
            L.dir.set(dd.x, 0.05, dd.z).normalize();
            L.color.setRGB(this.fog.color.r + toS(ex.x), this.fog.color.g + toS(ex.y), this.fog.color.b + toS(ex.z));
            L.power = Math.max(4, Math.min(64, 1 / Math.max(1e-3, dd.halfWidth * dd.halfWidth)));
          }
        }
      } else { this.stats.night = [0, 0, 0]; this.nightAerial.set(0, 0, 0); }
      // A SANDSTORM tints the fog orange-brown by the same light (a night storm reads dark).
      if (resolved.fogTintHex && resolved.dust > 0.02) {
        this._t.set(resolved.fogTintHex).multiplyScalar(diffuseRad * 1.4);
        this.fog.color.lerp(this._t, Math.min(0.9, resolved.dust * 0.95));
      }
    } else {
      this.stats.source = 'none';
    }

    // ── DENSITY ──
    const aerial = this.aerialLive();
    this.stats.aerial = aerial; this.stats.weatherDensity = weatherDensity;
    // Where the froxels run they ARE the clear-air haze (R5: no double-counted haze). THE VISIBILITY LAW
    // (2026-09-27): the biome floor inside `resolved.fogDensity` is clear air too — with the froxels live, only
    // its excess over the sky's own 40 km (a marsh's 12 km) stays as FogExp2; without them the haze is the larger
    // of the sky's and the biome's, never their sum (the sum was a 24 km day over a 60 km desert).
    const floor = resolved.clearAirDensity;
    const events = Math.max(0, weatherDensity - floor);
    const clearAir = aerial ? Math.max(0, floor - CLEAR_AIR_HAZE) : Math.max(CLEAR_AIR_HAZE, floor);
    this.fog.density = (clearAir + events) * activePreset().fogScale;
    this.fog.density *= farTierFog.factor;
    const env = worldEnv();
    this.fog.density *= env.fogMul;
    if (env.fogColorHex) this.fog.color.lerp(this._t.set(env.fogColorHex), TINT_BLEND);
    if (!distantDetailOn()) this.fog.density = Math.max(this.fog.density, fogWallDensity(currentDrawDistance()));
    if (underwaterOverride) {
      this.fog.color.setHex(underwaterOverride.color);
      this.fog.density = underwaterOverride.density;
    }

    // ── THE VALLEY MIST ──
    this.sampleFloor(seed);
    const meteo = meteoAt(seed, atlas.localX, atlas.localZ, now, tod);
    this.heightFog.mist = mistAmount(meteo.temperatureC - meteo.dewPointC, tod, resolved.wetness, meteo.wind01);
  }

  /** The lowest terrain in a 1 km window (and the 3 lowest cells, the hollows), re-sampled on movement. */
  private sampleFloor(seed: string): void {
    const px = atlas.localX, pz = atlas.localZ;
    const s = this.heightFog.sampledAt;
    if (Number.isFinite(s.x) && Math.hypot(px - s.x, pz - s.z) < FLOOR_RESAMPLE_M) return;
    s.x = px; s.z = pz;
    const pts: Array<{ x: number; y: number; z: number }> = [];
    const step = FLOOR_WINDOW_M / (FLOOR_GRID - 1);
    for (let i = 0; i < FLOOR_GRID; i++) for (let j = 0; j < FLOOR_GRID; j++) {
      const x = px - FLOOR_WINDOW_M / 2 + i * step, z = pz - FLOOR_WINDOW_M / 2 + j * step;
      const y = terrainHeight(seed, x, z);
      if (Number.isFinite(y)) pts.push({ x, y, z });
    }
    if (!pts.length) return;
    pts.sort((a, b) => a.y - b.y);
    // Sea level floors the anchor (the sea is the lowest ground there is).
    // sky/post (2026-09-27): the SEED's sea level, never 0 — the Commons sea stands at commonsSeaLevel (−163.5 m on
    // commons-genesis), so every shore and lowland under 0 m put the eye up to ~160 m BELOW the floor, where the fog is
    // e^(0.05·160) ≈ 3,000× its floor density: a whiteout by day and a black frame by night (the moon-road probe).
    const sea = commonsSeaLevel(seed);
    this.heightFog.floorAbsY = Math.max(sea, pts[0].y);
    this.heightFog.hollows = pts.slice(0, 3).map((p) => ({ x: p.x, y: Math.max(sea, p.y), z: p.z }));
  }

  /** Returns the terrainSeed for the 'commons' world — mirroring WeatherModule.commonsSeed. */
  private commonsSeed(conn: DbConnection): string {
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') return w.terrainSeed;
    return 'commons';
  }

  /** Test/inspection hooks. */
  get fogColorHex(): string { return '#' + this.fog.color.getHexString(); }
  /** The module's own fog colour object — LIVE even when `scene.fog` is detached (probe rigs, space, personal presets); the flat sky copies it. */
  get fogColor(): THREE.Color { return this.fog.color; }
  get fogDensity(): number { return this.fog.density; }
  applyForTest(tod: number, weather: string): void { const conn = atlas.conn; if (conn) this.applyFog(tod, weather, conn); }

  dispose(): void {
    if (this.meteoTimer !== null) { window.clearInterval(this.meteoTimer); this.meteoTimer = null; }
  }
}
